import { AuthServiceStrategy } from './auth-service-strategy';
import { Store } from '@ngrx/store';
import { EMPTY, from, map, Observable, of } from 'rxjs';
import { resetCartStates } from '../../stores/cart';
import { resetCouponStates } from '../../stores/coupon';
import { resetCustomerStates } from '../../stores/customer';
import { resetEmployeeStates } from '../../stores/employee';
import { resetGlobalRulesStates } from '../../stores/global';
import { resetHeldOrderStates } from '../../stores/order';
import { resetProductStates } from '../../stores/product';
import { resetPaymentStates } from '../../stores/payment';
import { Injectable } from '@angular/core';
import { LoginRequest } from '../../models/requests/login-request';
import { AuthType } from '../../shared/enums/AuthType.enum';
import { PermissionService } from '../permissions/permission.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { getAuthenticationBaseUrl } from '../../shared/utils/api-url.util';
import { KeycloakService } from './keycloak.service';

/** Web login redirects to Keycloak's hosted page (client `pos-web`); Electron/BASIC is untouched. */
@Injectable({
  providedIn: 'root',
})
export class BearerAuthService implements AuthServiceStrategy {
  readonly type = AuthType.BEARER;
  readonly baseUrl = getAuthenticationBaseUrl();

  constructor(
    private readonly keycloakService: KeycloakService,
    private store: Store,
    private readonly permissionService: PermissionService,
    private readonly localStorageService: LocalStorageService,
  ) {}

  buildAuthHeader(): string | null {
    const token = this.keycloakService.getToken();
    return token ? `${AuthType.BEARER} ${token}` : null;
  }

  /**
   * @deprecated Credential entry no longer happens in this app for Web — call
   * keycloakService.login() to redirect to Keycloak's hosted login page instead.
   */
  login(_request: LoginRequest): Observable<any> {
    this.keycloakService.login();
    return EMPTY;
  }

  isLoggedIn(): boolean {
    return this.keycloakService.isAuthenticated();
  }

  clearSession(): void {
    // Snapshot current user+terminal before wipe so next login can auto-select POS.
    this.captureRecentlyLoggedIn();

    this.store.dispatch(resetCartStates());
    this.store.dispatch(resetCouponStates());
    this.store.dispatch(resetCustomerStates());
    this.store.dispatch(resetEmployeeStates());
    this.store.dispatch(resetGlobalRulesStates());
    this.store.dispatch(resetHeldOrderStates());
    this.store.dispatch(resetProductStates());
    this.store.dispatch(resetPaymentStates());
    this.permissionService.clearPermissions();

    // Preserve recent_logged_in across the Keycloak redirect round-trip.
    const recentLoggedInData = localStorage.getItem('recent_logged_in');
    localStorage.clear();
    if (recentLoggedInData) {
      localStorage.setItem('recent_logged_in', recentLoggedInData);
    }
  }

  /**
   * Writes the active session into `recent_logged_in` when both user and terminal
   * are known. No-op when terminal was never chosen (e.g. failed permission load).
   */
  private captureRecentlyLoggedIn(): void {
    const userId = this.getLoggedInId();
    const terminal = this.localStorageService.getTerminal();
    const terminalId = terminal?.id ?? null;
    const terminalCode = terminal?.code ?? null;
    if (!userId || userId === '-1' || terminalId == null || !terminalCode) {
      return;
    }
    this.localStorageService.setRecentlyLoggedIn(
      userId,
      terminalId,
      terminalCode,
    );
  }

  logout(): void {
    this.clearSession();
    this.keycloakService.logout(globalThis.window.location.origin + '/login');
  }

  getLoggedInName(): string {
    const claims = this.keycloakService.getTokenParsed();
    return claims?.name ?? claims?.preferred_username ?? 'CS';
  }

  getLoggedInId(): string {
    return this.keycloakService.getTokenParsed()?.sub ?? '-1';
  }

  refreshToken(): Observable<void> {
    return from(this.keycloakService.updateToken(0)).pipe(map(() => void 0));
  }

  /** Refreshes the token if near expiry; false means no valid session (authGuard then logs out). */
  canLogin(): Observable<boolean> {
    if (!this.keycloakService.isAuthenticated()) {
      return of(false);
    }
    return from(this.keycloakService.updateToken(300)).pipe(
      map((token) => !!token),
    );
  }
}
